import React, { useState, useEffect, Fragment } from "react";
import { Col, Row, Alert, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getAllJobSeekers } from "../../../Services/LeaderJobSeeker";
import SupportTeamList from "../../../Components/SupportTeamList";
import { getAllSupportTeamMembers } from "../../../Services/LeaderSupportTeamMember";
import JobSeekersList from "../../../Components/JobSeekersList";
import {
  groupLeaderRecentJobsPerPage,
  groupLeaderRecentJSPerPage,
} from "../../../Utilities/Helper";
import { getAllJobs } from "../../../Services/AllJobsService";
import SpinnerLoader from "../../../Components/SpinnerLoader";
import moment from "moment";

import { getUserDetailsAPI } from "../../../Utilities/APIHelper";
import { get } from "../../../Services/AxiosService";
import WelcomeModal from "./WelcomeModal";
import SupportTeamOnboardingModal from "./SupportTeamOnboardingModal";
import TermsReacceptanceModal from "../../../Components/Shared/TermsReacceptanceModal";
import { useTermsAcceptance } from "../../../hooks/useTermsAcceptance";
import {
  checkSubscriptionStatus,
  createBillingPortalSession,
} from "../../../Services/StripeService";

export default function GroupLeaderDashboard() {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [supportTeamMemebers, setSupportTeamMemebers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showSpinnerForJS, setShowSpinnerForJS] = useState(true);
  const [showSpinnerForSTM, setShowSpinnerForSTM] = useState(true);
  const [showSpinnerForJobs, setShowSpinnerForJobs] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionChecking, setSubscriptionChecking] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [supportTeamCount, setSupportTeamCount] = useState(0);
  const [countLoaded, setCountLoaded] = useState(false);
  const [showSupportTeamOnboarding, setShowSupportTeamOnboarding] =
    useState(false);

  // Terms acceptance functionality
  const {
    showTermsModal,
    termsAccepted,
    acceptTerms,
    declineTerms,
    openTermsInNewWindow,
    currentTermsVersion,
  } = useTermsAcceptance(email);

  // Verify subscription status via API
  useEffect(() => {
    async function checkSubscription() {
      if (email) {
        try {
          setSubscriptionChecking(true);
          const response = await checkSubscriptionStatus(email);
          setHasSubscription(response.hasActiveSubscription);
          if (response.subscriptionDetails) {
            setSubscriptionDetails(response.subscriptionDetails);
          }
          setSubscriptionChecking(false);
          if (response.error) {
            setSubscriptionError(response.error);
          }
        } catch (err) {
          console.error("Error checking subscription:", err);
          setSubscriptionChecking(false);
          setSubscriptionError("Failed to verify subscription status");
        }
      }
    }

    if (email) {
      checkSubscription();
    }
  }, [email]);

  useEffect(() => {
    async function jobSeeksersListData() {
      getAllJobSeekers("recordperpage=" + groupLeaderRecentJSPerPage)
        .then((res) => {
          const result = JSON.parse(res.response);
          setJobSeekers(result.jobSeekers);
          setShowSpinnerForJS(false);
        })
        .catch((err) => {
          console.log("JobSeeker: ", err);
          setShowSpinnerForJS(false);
        });
    }
    jobSeeksersListData();
  }, []);

  useEffect(() => {
    async function jobsData() {
      const filter = "recordperpage=" + groupLeaderRecentJobsPerPage;
      getAllJobs(filter)
        .then((res) => {
          const result = JSON.parse(res.response);
          setJobs(result.jobs);
          setShowSpinnerForJobs(false);
        })
        .catch((err) => {
          console.log("Jobs: ", err);
          setShowSpinnerForJobs(false);
        });
    }
    jobsData();
  }, []);

  useEffect(() => {
    async function supportTeamMemberListData() {
      const filter = "recordperpage=" + groupLeaderRecentJSPerPage;
      getAllSupportTeamMembers(filter)
        .then((res) => {
          const result = JSON.parse(res.response);
          setSupportTeamMemebers(result.supportTeamMembers);

          // Store the active support team count for subscription messaging
          if (result && result.activeSupportTeamCount !== undefined) {
            setSupportTeamCount(result.activeSupportTeamCount);
          }
          setCountLoaded(true);
          setShowSpinnerForSTM(false);
        })
        .catch((err) => {
          console.log("Sponsorship: ", err);
          setShowSpinnerForSTM(false);
          setCountLoaded(true);
        });
    }
    supportTeamMemberListData();
  }, []);

  useEffect(() => {
    async function getUserInfo() {
      get(getUserDetailsAPI)
        .then((res) => {
          let response = res.data.response;
          setName(response.firstName);
          setEmail(response.email);
        })
        .catch((err) => {
          console.log(err);
        });
    }
    getUserInfo();
  }, []);

  // Show onboarding modal if first time and no support team members
  useEffect(() => {
    if (
      countLoaded &&
      supportTeamMemebers.length === 0 &&
      !localStorage.getItem("supportTeamOnboardingShown")
    ) {
      setShowSupportTeamOnboarding(true);
    }
  }, [countLoaded, supportTeamMemebers.length]);

  const handleSupportTeamOnboardingFinish = (dontShowAgain) => {
    setShowSupportTeamOnboarding(false);
    if (dontShowAgain) {
      localStorage.setItem("supportTeamOnboardingShown", "true");
    }
  };

  const handleManageSubscription = async () => {
    if (!subscriptionDetails?.customerId) {
      setSubscriptionError("Customer ID not found. Please contact support.");
      return;
    }

    setManagingSubscription(true);
    try {
      await createBillingPortalSession(subscriptionDetails.customerId);
      // Redirect happens in service function
    } catch (err) {
      console.error("Error opening billing portal:", err);
      setManagingSubscription(false);
      setSubscriptionError(
        "Failed to open billing portal. Please try again later."
      );
    }
  };

  return (
    <React.Fragment>
      <WelcomeModal
        show={showWelcomeModal}
        onHide={() => setShowWelcomeModal(false)}
        userName={name}
      />
      <SupportTeamOnboardingModal
        show={showSupportTeamOnboarding}
        onHide={() => setShowSupportTeamOnboarding(false)}
        onFinish={handleSupportTeamOnboardingFinish}
      />
      <TermsReacceptanceModal
        show={showTermsModal}
        onAccept={acceptTerms}
        onDecline={declineTerms}
        onOpenTerms={openTermsInNewWindow}
        currentVersion={currentTermsVersion}
      />
      <div className="container p-0">
        <h1 className="page-title">
          Hello {name} <small>(Group leader)</small>
        </h1>

        {subscriptionChecking && (
          <div className="mb-4 text-center">
            <SpinnerLoader />
            <p>Verifying subscription status...</p>
          </div>
        )}

        {!subscriptionChecking && subscriptionError && (
          <Alert variant="danger" className="mb-4">
            <strong>Error</strong>
            <p>{subscriptionError}</p>
          </Alert>
        )}

        {!subscriptionChecking && !hasSubscription && !subscriptionError && (
          <Alert variant="warning" className="mb-4">
            <strong>
              {countLoaded && supportTeamCount <= 100
                ? "Subscription Activation Required"
                : "Subscription Required"}
            </strong>
            <p className="mb-1">
              {countLoaded && supportTeamCount <= 100 ? (
                <>
                  Please activate your JobInfoNetwork subscription. Based on
                  your current team size (
                  <strong>{supportTeamCount} Support Team Members</strong>), you
                  qualify for a <strong>free tier</strong>.
                </>
              ) : (
                <>
                  Please subscribe to access all JobInfoNetwork features. Based
                  on your current team size (
                  <strong>{supportTeamCount} members</strong>), a paid
                  subscription will be required.
                </>
              )}
            </p>
            <Link
              to={`${process.env.PUBLIC_URL}/groupleader/subscription`}
              className="btn btn-warning btn-sm mt-2"
            >
              {countLoaded && supportTeamCount <= 100
                ? "Activate Subscription"
                : "Subscribe Now"}
            </Link>
          </Alert>
        )}

        {!subscriptionChecking && hasSubscription && subscriptionDetails && (
          <Alert variant="success" className="mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Active Subscription</strong>
                <p className="mb-0">
                  Your {subscriptionDetails.plan} subscription is active.
                  {subscriptionDetails.nextBillingDate &&
                    ` Next billing date: ${new Date(
                      subscriptionDetails.nextBillingDate
                    ).toLocaleDateString()}`}
                </p>
              </div>
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleManageSubscription}
                disabled={managingSubscription}
              >
                {managingSubscription ? "Opening..." : "Manage Subscription"}
              </Button>
            </div>
          </Alert>
        )}

        <div className="dashboard-wrapper">
          <Row>
            <Col md={6}>
              <div className="dashboard-box resume dashboard-col-bg sponser-dashboard-col hide-from-dashboard job-seeker-dashboard-list">
                <h2 className="page-sub-title">Job Seekers</h2>
                <JobSeekersList
                  jobSeekersList={jobSeekers}
                  showSpinner={showSpinnerForJS}
                />
                <Link
                  className="btn btn-warning btn-xs white mt-3"
                  to={`${process.env.PUBLIC_URL}/groupleader/jobseekers`}
                >
                  <span className="icon icon-job-seeker mr-2"> </span> View all
                  job seekers
                </Link>
              </div>

              <div className="dashboard-box resume dashboard-col-bg sponser-dashboard-col hide-from-dashboard mt-5 support-team-dashboard-list">
                <h2 className="page-sub-title">Support Team Members</h2>
                <SupportTeamList
                  supportTeamMembers={supportTeamMemebers}
                  showSpinner={showSpinnerForSTM}
                />
                <Link
                  className="btn btn-warning btn-xs white mt-3"
                  to={`${process.env.PUBLIC_URL}/groupleader/supportteammembers`}
                >
                  <span className="icon icon-support-members mr-2"></span> View
                  all Support Team Members{" "}
                </Link>
              </div>
            </Col>

            <Col md={6}>
              <div className="dashboard-box resume dashboard-col-bg sponser-dashboard-col hide-from-dashboard">
                <h2 className="page-sub-title">New Job Posting</h2>
                <ul className="resume-list job-list">
                  {showSpinnerForJobs && <SpinnerLoader />}
                  {jobs.length === 0 && !showSpinnerForJobs && (
                    <h5 style={{ textAlign: "center" }}>No Data Found</h5>
                  )}
                  {jobs.map((item, index) => (
                    <li key={item.jobId}>
                      <div className="resume-details">
                        <Link
                          to={`${process.env.PUBLIC_URL}/groupleader/groupleaderjobdetails/${item.jobId}`}
                        >
                          <h4>{item.positionTitle}</h4>
                        </Link>
                        <h5>{item.companyName}</h5>
                        <div className="date-location">
                          Posted :{" "}
                          <span className="text">
                            {moment(item.jobStartDate).format("MMMM DD, Y")}
                          </span>
                        </div>
                        <div className="date-location">
                          Location :{" "}
                          <span className="text">
                            {item.city === null || item.city === ""
                              ? "-"
                              : item.city + ", " + item.stateName === null ||
                                item.stateName === ""
                              ? "-"
                              : item.stateName}
                          </span>
                          {item.jobDescription !== null && (
                            <Fragment>
                              {item.jobDescription.length <= 135 && (
                                <p className="discription">
                                  {item.jobDescription}
                                </p>
                              )}
                              {item.jobDescription.length > 135 && (
                                <p className="discription">
                                  {item.jobDescription.substr(0, 134) + "..."}
                                </p>
                              )}
                            </Fragment>
                          )}
                          <div>
                            <Link
                              to={`${process.env.PUBLIC_URL}/groupleader/groupleaderjobdetails/${item.jobId}`}
                              className="btn-link"
                            >
                              Read more.
                            </Link>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <Link
                  className="btn btn-warning btn-xs white mt-3"
                  to={`${process.env.PUBLIC_URL}/groupleader/groupleaderjobs`}
                >
                  <span className="icon icon-support-members mr-2"></span> View
                  all jobs{" "}
                </Link>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
}
